/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { useTranslations } from "@i18n/react";
import { HallowABFlag, type MixpanelContext } from "@packages/ab";
import type { AnalyticsContext } from "@packages/analytics";
import { HallowAnalyticsEvent } from "@packages/analytics";
import { Link } from "@packages/framework";
import type {
  AssignmentSectionItem,
  TheWordSectionItem,
  TriviaSectionItem,
} from "@packages/sdk";
import { getURLFromDeeplink, useBranch } from "@packages/sdk";
import type {
  CarouselApi,
  CategoryMediumProps,
  ContentLargeProps,
  ContentMediumProps,
  ContentSmallProps,
  GridProps,
  KeyedElement,
} from "@packages/ui";
import {
  Assignment,
  Button,
  CampaignLargeWithAuthor,
  CampaignMedium,
  Card,
  CategoriesGameMedium,
  CategoryLarge,
  CategoryMedium,
  ChallengeLarge,
  ChevronRightIcon,
  ContentLarge,
  ContentMedium,
  ContentSmall,
  DailyQuote,
  Edit2Icon,
  IconButton,
  InfoIcon,
  MoreIcon,
  PinIcon,
  PlusIcon,
  PrayIcon,
  ReflectionIcon,
  RoutinesIcon,
  ShareIcon,
  ShareModal,
  spacing,
  StarIcon,
  Support1Icon,
  TextFirstSectionItem,
  TheWordLarge,
  TheWordMedium,
  TimeIcon,
  TriviaLarge,
  TriviaMedium,
  useModal,
} from "@packages/ui";
import type { format } from "date-fns/format";
import type { FC, PropsWithChildren } from "react";
import { Fragment } from "react";

import {
  getSectionItemCategoriesGameProps,
  getSmallSectionItemCategoriesProps,
} from "./getCategoriesSectionItemProps";
import { getRoutineSectionItemProps } from "./getRoutineSectionItemProps";
import { getSectionItemAssignmentProps } from "./getSectionItemAssignmentProps";
import {
  getSectionItemCampaignMediumProps,
  getSectionItemCampaignProps,
} from "./getSectionItemCampaignProps";
import { getSectionItemCategoryProps } from "./getSectionItemCategoryProps";
import { getSectionItemChallengeProps } from "./getSectionItemChallengeProps";
import { getSectionItemContentProps } from "./getSectionItemContentProps";
import { getSectionItemDailyQuoteProps } from "./getSectionItemDailyQuoteProps";
import {
  getSectionItemTheWordProps,
  getSmallSectionItemTheWordProps,
} from "./getSectionItemTheWordProps";
import {
  getSectionItemTriviaProps,
  getSmallSectionItemTriviaProps,
} from "./getSectionItemTriviaProps";
import type { SectionProps } from "./Section.emotion";

export type GetSectionItemsProps = Pick<
  SectionProps,
  | "itemTypeLinkSearchParams"
  | "link"
  | "mutate"
  | "section"
  | "page"
  | "screenName"
  | "showUnsupportedSections"
> & {
  analytics?: AnalyticsContext;
  isKidAccount?: boolean;
  t: ReturnType<typeof useTranslations>;
  isDesktop: boolean;
  format?: typeof format;
  flags?: MixpanelContext["flags"];
};

const titleLinkStyle: Interpolation = {
  marginLeft: spacing.negativeM,
};

export const getSectionItems = ({
  analytics,
  isKidAccount,
  itemTypeLinkSearchParams,
  link,
  mutate,
  section,
  page,
  screenName,
  t,
  isDesktop,
  format,
  showUnsupportedSections,
  flags,
}: GetSectionItemsProps) => {
  let items: KeyedElement[] | null;
  const rightActions: KeyedElement[] = [];
  let size: CarouselApi["size"] | GridProps["size"] = "m";
  let TitleLink: FC<PropsWithChildren> = Fragment;

  const shareModal = useModal(ShareModal);
  const branch = useBranch();

  if (section.id === 1310) return { items: [], rightActions, size, TitleLink };

  const supportedSectionItems = showUnsupportedSections
    ? section.items
    : section.items.filter(
        (item) => item.reference_type !== "community_challenge",
      );

  const showRoutines = !!flags?.[HallowABFlag.WebRoutines]?.isTargeted;

  switch (section.item_type) {
    case "campaign":
      items = supportedSectionItems
        .map((item) =>
          item.item_type !== section.item_type ? null : (
            <CampaignLargeWithAuthor
              {...getSectionItemCampaignProps({
                analytics,
                item,
                link,
                section,
                page,
                screenName,
                t,
              })}
              data-id={item.id}
              key={`${section.id}_${item.id}`}
            />
          ),
        )
        .filter((section) => !!section);
      size = "xl";
      break;
    case "daily_quote":
      items = supportedSectionItems.map((item) => (
        <DailyQuote
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...getSectionItemDailyQuoteProps({
            isKidAccount,
            item,
            section,
            t,
            onShare: async () =>
              item?.reference?.shareable_url
                ? shareModal.show({
                    title: item?.reference?.text,
                    description: item?.reference?.attributed_to,
                    link: await branch.dailyQuote({
                      id: item.reference_id,
                      url: item.reference.shareable_url,
                    }),
                    imgSrc: item?.reference?.image_url,
                    color: "#000",
                  })
                : null,
          })}
        />
      ));
      size = "xl";
      break;
    case "deeplink_card":
    case "deeplink_card_medium":
    case "deeplink_card_small":
      let CategoryComponent: typeof CategoryLarge | typeof CategoryMedium;

      if (section.item_type === "deeplink_card") {
        CategoryComponent = CategoryLarge;
        size = "m";
      } else {
        CategoryComponent = CategoryMedium;
        size = "s";
      }

      items = supportedSectionItems.map((item) => (
        <CategoryComponent
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...(getSectionItemCategoryProps({
            analytics,
            item,
            link,
            page,
            screenName,
            section,
            size: section.item_type === "deeplink_card" ? "l" : "m",
            t,
          }) as CategoryMediumProps)}
        />
      ));
      break;
    case "large":
      items = supportedSectionItems.map((item) => {
        if (
          item.reference_type === "challenge" ||
          item.reference_type === "collection_and_session" ||
          (item.reference_type === "collection" &&
            item.reference.type === "challenge")
        )
          return (
            <ChallengeLarge
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSectionItemChallengeProps({
                analytics,
                item,
                link,
                mutate,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "routine_item") {
          if (showRoutines)
            return (
              <ContentMedium
                key={`${section.id}_${item.id}`}
                {...getRoutineSectionItemProps({
                  analytics,
                  format,
                  item,
                  link,
                  page,
                  screenName,
                  section,
                  t,
                })}
              />
            );
          return null;
        }

        return (
          <ContentLarge
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...(getSectionItemContentProps({
              analytics,
              item,
              link,
              page,
              screenName,
              section,
              size: "l",
              t,
            }) as ContentLargeProps)}
          />
        );
      });
      size =
        supportedSectionItems[0]?.reference_type === "routine_item" &&
        showRoutines
          ? "m"
          : "xl";
      break;
    case "medium":
      items = supportedSectionItems.map((item) => {
        if (item.reference_type === "categories_game")
          return (
            <ContentMedium
              {...getSmallSectionItemCategoriesProps({
                analytics,
                item,
                link,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "the_word")
          return (
            <ContentMedium
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSmallSectionItemTheWordProps({
                analytics,
                item,
                link,
                page,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "trivia")
          return (
            <ContentMedium
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSmallSectionItemTriviaProps({
                analytics,
                format,
                item,
                link,
                page,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "trivia_leaderboard")
          return (
            <ContentMedium
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSmallSectionItemTriviaProps({
                analytics,
                format,
                item: {
                  ...item,
                  type: "trivia",
                  reference_type: "trivia",
                  reference: item.reference.game,
                },
                link,
                page,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "campaign")
          return (
            <CampaignMedium
              data-id={item.id}
              key={`${section.id}_${item.id}`}
              {...getSectionItemCampaignMediumProps({
                item,
                link,
                page,
                screenName,
                section,
                t,
              })}
            />
          );

        if (item.reference_type === "routine_item") {
          if (showRoutines)
            return (
              <ContentMedium
                data-id={item.id}
                key={`${section.id}_${item.id}`}
                {...getRoutineSectionItemProps({
                  analytics,
                  format,
                  item,
                  link,
                  page,
                  screenName,
                  section,
                  t,
                })}
              />
            );
          return null;
        }
        return (
          <ContentMedium
            key={`${section.id}_${item.id}`}
            {...(getSectionItemContentProps({
              analytics,
              item,
              itemTypeLinkSearchParams,
              link,
              page,
              screenName,
              section,
              size: "m",
              t,
            }) as ContentMediumProps)}
          />
        );
      });
      size = "s";
      break;
    case "player_card":
      items = supportedSectionItems.map((item) =>
        item.reference_type === "challenge" ||
        item.reference_type === "collection_and_session" ||
        (item.reference_type === "collection" &&
          item.reference.type === "challenge") ? (
          <ChallengeLarge
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemChallengeProps({
              analytics,
              item,
              link,
              mutate,
              page,
              screenName,
              section,
              t,
            })}
          />
        ) : null,
      );
      size = "xl";
      break;
    case "small":
      items = supportedSectionItems.map((item) => (
        <ContentSmall
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...(getSectionItemContentProps({
            analytics,
            item,
            link,
            page,
            screenName,
            section,
            size: "s",
            t,
          }) as ContentSmallProps)}
        />
      ));
      size = "l";
      break;
    case "trivia":
      items = supportedSectionItems.map((item) => (
        <TriviaLarge
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...getSectionItemTriviaProps({
            analytics,
            item: item as TriviaSectionItem,
            link,
            page,
            screenName,
            section,
            t,
          })}
        />
      ));
      size = "xl";
      break;
    case "trivia_leaderboard":
      items = supportedSectionItems.map((item) => (
        <TriviaLarge
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...getSectionItemTriviaProps({
            analytics,
            item: {
              ...item,
              type: "trivia",
              reference_type: "trivia",
              reference: item.reference.game,
            } as TriviaSectionItem,
            link,
            page,
            screenName,
            section,
            t,
          })}
        />
      ));
      size = "xl";
      break;
    case "text_first_content":
      items = supportedSectionItems.map((item) =>
        item.reference_type === "text_first_content" ? (
          <TextFirstSectionItem
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...item.reference}
          />
        ) : null,
      );
      size = "m";
      break;
    case "large_with_header":
    case "medium_with_header":
    case "assignment":
      items = supportedSectionItems.map((item) => (
        <Assignment
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...getSectionItemAssignmentProps({
            analytics,
            format,
            item: item as AssignmentSectionItem,
            itemTypeLinkSearchParams,
            page,
            screenName,
            section,
            t,
          })}
        />
      ));
      size = isDesktop
        ? section.item_type === "large_with_header"
          ? "m"
          : "s"
        : "l";
      break;
    case "the_word":
      items = supportedSectionItems.map((item) => (
        <TheWordLarge
          data-id={item.id}
          key={`${section.id}_${item.id}`}
          {...getSectionItemTheWordProps({
            analytics,
            item: item as TheWordSectionItem,
            link,
            page,
            screenName,
            section,
            t,
          })}
        />
      ));
      size = "xl";
      break;
    case "games":
      items = supportedSectionItems.map((item) =>
        item.reference_type === "categories_game" ? (
          <CategoriesGameMedium
            key={`${section.id}_${item.id}`}
            {...getSectionItemCategoriesGameProps({
              analytics,
              item,
              screenName,
              section,
              t,
            })}
          />
        ) : item.reference_type === "the_word" ? (
          <TheWordMedium
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemTheWordProps({
              analytics,
              item: item as TheWordSectionItem,
              link,
              page,
              screenName,
              section,
              t,
            })}
          />
        ) : item.reference_type === "trivia" ? (
          <TriviaMedium
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemTriviaProps({
              analytics,
              item,
              link,
              screenName,
              section,
              t,
            })}
          />
        ) : item.reference_type === "trivia_leaderboard" ? (
          <TriviaMedium
            data-id={item.id}
            key={`${section.id}_${item.id}`}
            {...getSectionItemTriviaProps({
              analytics,
              item: {
                ...item,
                type: "trivia",
                reference_type: "trivia",
                reference: item.reference.game,
              } as TriviaSectionItem,
              link,
              screenName,
              section,
              t,
            })}
          />
        ) : null,
      );
      break;
    case "create_campaign_prompt":
    case "create_campaign_prompt_v2_final":
    case "magisterium_prompt":
    case "streak":
    case "text_summary":
    case "deeplink_card_condensed":
    default:
      if (showUnsupportedSections) {
        items = supportedSectionItems.map((item) => (
          <Card
            key={`${section.id}_${item.id}`}
            imgSrc={item.reference?.image_url}
            title={
              item.reference?.title ??
              `Unsupported ${item.item_type} section item`
            }
            description={
              <div>
                <p>
                  {item.reference?.description ??
                    item.reference?.detailed_desc ??
                    item.reference?.short_desc ??
                    item.reference?.label_desc}
                </p>
                <p>This item is not supported on web.</p>
              </div>
            }
            data-id={item.id}
          />
        ));
      }
      break;
  }

  if (section.action_link) {
    const onClickCapture = () => {
      analytics?.track({
        event: HallowAnalyticsEvent.TappedSectionActionButton,
        properties: {
          page_id: page?.id,
          section_id: section.id,
          section_title: section.title,
          section_type: section.type,
          text: section.action_title,
          icon: section.action_icon,
          link: section.action_link,
          section_cohort_id: section?.cohorts?.map((c) => c.id) ?? null,
          section_cohort: section?.cohorts?.map((c) => c.name) ?? null,
        },
      });
    };
    const url = getURLFromDeeplink({ deeplink: section.action_link });
    // exclude routine creation/editing for now
    if (!url.includes("routine/items/create")) {
      if (section.action_icon) {
        let icon: KeyedElement | null = null;
        switch (section.action_icon) {
          case "plus":
            icon = <PlusIcon.Simple />;
            break;
          case "plus_outline":
            icon = <PlusIcon.Outline />;
            break;
          case "plus_filled":
            icon = <PlusIcon.Filled />;
            break;
          case "edit":
            icon = <Edit2Icon />;
            break;
          case "more":
            icon = <MoreIcon />;
            break;
          case "info":
            icon = <InfoIcon />;
            break;
          case "support":
            icon = <Support1Icon />;
            break;
          case "share":
            icon = <ShareIcon />;
            break;
          case "pin_outline":
            icon = <PinIcon.Outline />;
            break;
          case "pin_filled":
            icon = <PinIcon.Filled />;
            break;
          case "favorite_outline":
            icon = <StarIcon.Outline />;
            break;
          case "favorite_filled":
            icon = <StarIcon.Filled />;
            break;
          case "favorite_off":
            icon = <StarIcon.Off />;
            break;
          case "time":
            icon = <TimeIcon />;
            break;
          case "pray_outline":
            icon = <PrayIcon.Outline />;
            break;
          case "pray_filled":
            icon = <PrayIcon.Filled />;
            break;
          case "reflection":
            icon = <ReflectionIcon />;
            break;
          case "routine":
            icon = <RoutinesIcon />;
            break;
          case "download":
          // we don't support downloading
          default:
        }
        if (icon) {
          rightActions.push(
            <IconButton
              key="see_all"
              asChild
              onClickCapture={onClickCapture}
              size="s"
              variant="outline"
              icon={icon}
              title={section.action_title}
            >
              <Link href={url} />
            </IconButton>,
          );
        }
      } else {
        rightActions.push(
          <Button
            key="see_all"
            asChild
            onClickCapture={onClickCapture}
            size="s"
            variant="outline"
          >
            <Link href={url}>{section.action_title}</Link>
          </Button>,
        );
      }
    }
  }

  if (section.title_link && !section.title_link.includes("leaderboard")) {
    TitleLink = ({ children }: PropsWithChildren) => (
      <Button
        asChild
        variant={"transparentPrimary"}
        endIcon={<ChevronRightIcon />}
        css={titleLinkStyle}
        onClickCapture={() =>
          analytics.track({
            event: HallowAnalyticsEvent.TappedSectionSeeAll,
            properties: {
              page_id: page?.id,
              section_id: section.id,
              section_title: section.title,
              section_type: section.type,
              accessibility_text: section.title_link_accessibility,
              link: section.title_link,
              section_cohort_id: section?.cohorts?.map((c) => c.id) ?? null,
              section_cohort: section?.cohorts?.map((c) => c.name) ?? null,
            },
          })
        }
      >
        <Link
          href={getURLFromDeeplink({ deeplink: section.title_link })}
          title={section.title_link_accessibility}
        >
          {children}
        </Link>
      </Button>
    );
    TitleLink.displayName = "SectionTitleLink";
  }

  return { items: items?.filter(Boolean), rightActions, size, TitleLink };
};
